import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Props {
  thinking: string
  isStreaming?: boolean
}

/**
 * Collapsible extended-thinking block. Streams open while the model is
 * actively thinking; collapses to a 1-line summary once complete.
 * Matches the visual language of ZinsserThinkingBlock — small uppercase
 * label + amber dot — so a user moving between tools doesn't feel a
 * stylistic shift.
 */
export function ConciergeThinkingBlock({ thinking, isStreaming = false }: Props) {
  const [expanded, setExpanded] = useState(false)
  const hasContent = thinking.trim().length > 0

  if (!hasContent && !isStreaming) return null

  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 text-[10px] font-light uppercase tracking-[0.08em] text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronRight
          size={10}
          className={cn(
            'transition-transform duration-200',
            expanded && 'rotate-90'
          )}
        />
        <span
          className={cn(
            'inline-block w-1.5 h-1.5 rounded-full',
            isStreaming ? 'bg-amber-500 animate-pulse' : 'bg-muted-foreground/40'
          )}
        />
        <span>{isStreaming ? 'Thinking…' : 'Thought'}</span>
      </button>
      {expanded && hasContent && (
        <div className="mt-2 pl-4 border-l border-border/60 text-[12px] text-muted-foreground whitespace-pre-wrap leading-relaxed font-light">
          {thinking.trim()}
        </div>
      )}
    </div>
  )
}
